import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { withAuthRecovery } from '@/lib/authRecovery'
import { MetricPeriod } from '@/lib/utils'
import { getLocalIsoDate } from '@/lib/analytics/periodCalculations'
import {
  getGlobalPeriodSelection,
  getStudioPeriodSelection,
  setGlobalPeriodSelection,
  setStudioPeriodSelection
} from '@/lib/studioPeriodSelection'

export interface UseStudioPeriodsReturn {
  currentPeriod: MetricPeriod | null
  availablePeriods: MetricPeriod[]
  currentPeriodIndex: number
  loading: boolean
  error: string | null
  isCurrentPeriod: boolean
  canGoPrev: boolean
  canGoNext: boolean
  goToPrevPeriod: () => void
  goToNextPeriod: () => void
  setPeriodByIndex: (index: number) => void
}

/**
 * Hook для управления периодами аналитики студии
 * Загружает доступные периоды, управляет текущим периодом и навигацией
 */
export function useStudioPeriods(studioId?: number): UseStudioPeriodsReturn {
  const [currentPeriod, setCurrentPeriod] = useState<MetricPeriod | null>(null)
  const [availablePeriods, setAvailablePeriods] = useState<MetricPeriod[]>([])
  const [currentPeriodIndex, setCurrentPeriodIndex] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Загрузка доступных периодов
  useEffect(() => {
    loadAvailablePeriods()
  }, [])

  const loadAvailablePeriods = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const { data, error: fetchError } = await withAuthRecovery(() =>
        supabase
          .from('metric_periods')
          .select('*')
          .order('period_start', { ascending: true })
      )
      
      if (fetchError) {
        throw fetchError
      }
      
      if (data && data.length > 0) {
        setAvailablePeriods(data as MetricPeriod[])
        // Связка глобального и студийного селекторов:
        // 1) если есть глобальный выбор периода — приоритет у него;
        // 2) иначе используем персональный выбор студии (если есть).
        const globalSavedPeriodId = getGlobalPeriodSelection()
        const studioSavedPeriodId = studioId ? getStudioPeriodSelection(studioId) : null
        const savedPeriodId = globalSavedPeriodId ?? studioSavedPeriodId
        const savedIndex = typeof savedPeriodId === 'number'
          ? data.findIndex((p: MetricPeriod) => p.period_id === savedPeriodId)
          : -1
        
        // Находим текущий период
        const today = getLocalIsoDate()
        const currentIndex = data.findIndex((p: MetricPeriod) => 
          p.period_start <= today && today <= p.period_end
        )
        const selectedIndex = savedIndex >= 0
          ? savedIndex
          : (currentIndex >= 0 ? currentIndex : data.length - 1)
        
        setCurrentPeriodIndex(selectedIndex)
        setCurrentPeriod(data[selectedIndex] as MetricPeriod)
      }
    } catch (err) {
      console.error('Error loading periods:', err)
      setError(err instanceof Error ? err.message : 'Failed to load periods')
    } finally {
      setLoading(false)
    }
  }

  // Проверка, является ли выбранный период текущим
  const isCurrentPeriod = useCallback(() => {
    if (!currentPeriod) return false
    const today = getLocalIsoDate()
    return currentPeriod.period_start <= today && today <= currentPeriod.period_end
  }, [currentPeriod])

  // Можно ли перейти к предыдущему периоду
  const canGoPrev = currentPeriodIndex > 0

  // Можно ли перейти к следующему периоду (только если он не в будущем)
  const canGoNext = useCallback(() => {
    if (currentPeriodIndex >= availablePeriods.length - 1) return false
    const nextPeriod = availablePeriods[currentPeriodIndex + 1]
    const today = getLocalIsoDate()
    return nextPeriod && nextPeriod.period_start <= today
  }, [currentPeriodIndex, availablePeriods])

  // Навигация к предыдущему периоду
  const goToPrevPeriod = useCallback(() => {
    if (canGoPrev) {
      const newIndex = currentPeriodIndex - 1
      setCurrentPeriodIndex(newIndex)
      setCurrentPeriod(availablePeriods[newIndex])
    }
  }, [currentPeriodIndex, availablePeriods, canGoPrev])

  // Навигация к следующему периоду
  const goToNextPeriod = useCallback(() => {
    if (canGoNext()) {
      const newIndex = currentPeriodIndex + 1
      setCurrentPeriodIndex(newIndex)
      setCurrentPeriod(availablePeriods[newIndex])
    }
  }, [currentPeriodIndex, availablePeriods, canGoNext])

  // Установка периода по индексу
  const setPeriodByIndex = useCallback((index: number) => {
    if (index >= 0 && index < availablePeriods.length) {
      setCurrentPeriodIndex(index)
      setCurrentPeriod(availablePeriods[index])
    }
  }, [availablePeriods])

  useEffect(() => {
    if (!currentPeriod?.period_id) return
    setGlobalPeriodSelection(currentPeriod.period_id)
    if (studioId) {
      setStudioPeriodSelection(studioId, currentPeriod.period_id)
    }
  }, [studioId, currentPeriod?.period_id])

  return {
    currentPeriod,
    availablePeriods,
    currentPeriodIndex,
    loading,
    error,
    isCurrentPeriod: isCurrentPeriod(),
    canGoPrev,
    canGoNext: canGoNext(),
    goToPrevPeriod,
    goToNextPeriod,
    setPeriodByIndex
  }
}
